// PAY2-S6 (docs/build-briefs/PAY2-S6-royalty-collection-authorization.md; DEC-406). The pure rules behind the "Royalty
// payments" wizard step and the Settings row: a franchise location authorizes its franchisor to debit its bank by ACH
// for the monthly royalty, on Stripe's own page. Labels and states only (the owner's copy rule); the one sentence shown
// is the authorization text the server sends and hashes, which the signer must see. No DOM, so it is tested directly.
// Nothing here names an account, a customer or a payment method: the server sends none.

import type { RoyaltyAuthorizationDto } from "./api.ts";

/** What the person sees the state as. */
export type RoyaltyPhase = "ready" | "on" | "needs";

export function royaltyPhase(dto: RoyaltyAuthorizationDto | undefined): RoyaltyPhase {
  if (dto?.status === "active") return "on";
  if (dto?.status === "needs_reauthorization") return "needs";
  return "ready";
}

/** Whether the step and the row are offered at all. The server decides: nothing is drawn for anything else. */
export function royaltyOffered(dto: RoyaltyAuthorizationDto | undefined): boolean {
  return dto?.applies === true;
}

/** "Account ending 1234", or null with no bank. */
export function bankLine(last4: string | null | undefined): string | null {
  return typeof last4 === "string" && /^\d{4}$/.test(last4) ? `Account ending ${last4}` : null;
}

/** "Oct 8, 2026" from an ISO instant, in the browser's zone. */
export function signedOnText(iso: string | null | undefined): string | null {
  if (typeof iso !== "string") return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export interface RoyaltyRow {
  label: string;
  value: string;
  tag?: { label: string; ok: boolean };
}

export interface RoyaltyStepView {
  title: string;
  tag: { label: string; ok: boolean };
  rows: RoyaltyRow[];
  /** The authorization text to show above the button, or null once it is on. */
  disclosure: string | null;
  primaryLabel: string;
  primaryEnabled: boolean;
  primaryAction: "stripe" | "next";
  skip: boolean;
  /** The reason the primary is dimmed, or "". */
  blockedReason: string;
}

export const ROYALTY_NOT_ALLOWED = "Not allowed";

export function royaltyStepView(input: {
  dto: RoyaltyAuthorizationDto | undefined;
  /** The session holds admin.payments.onboard. */
  canSign: boolean;
  /** The browser came back from Stripe without finishing. */
  cameBack: boolean;
}): RoyaltyStepView {
  const phase = royaltyPhase(input.dto);
  const bank = bankLine(input.dto?.bankLast4);
  const signedOn = signedOnText(input.dto?.acceptedAt);
  const signer = input.dto?.signerName ?? null;
  if (phase === "on") {
    const rows: RoyaltyRow[] = [
      { label: "Bank", value: bank ?? "On file", tag: { label: "Done", ok: true } },
    ];
    if (signer !== null) rows.push({ label: "Signed by", value: signer });
    if (signedOn !== null) rows.push({ label: "Date", value: signedOn });
    return {
      title: "Royalty payments are on",
      tag: { label: "On", ok: true },
      rows,
      disclosure: null,
      primaryLabel: "Continue",
      primaryEnabled: true,
      primaryAction: "next",
      skip: false,
      blockedReason: "",
    };
  }
  const rows: RoyaltyRow[] = [];
  if (phase === "needs") {
    rows.push({
      label: "Bank",
      value: bank ?? "On file",
      tag: { label: "Needs update", ok: false },
    });
  } else {
    rows.push({ label: "Bank", value: "Not chosen", tag: { label: "To do", ok: false } });
  }
  return {
    title: phase === "needs" ? "Update your bank account" : "Royalty payments",
    tag: phase === "needs" ? { label: "Needs update", ok: false } : { label: "To do", ok: false },
    rows,
    disclosure: input.dto?.mandate?.text ?? null,
    primaryLabel: input.cameBack || phase === "needs" ? "Back to Stripe" : "Continue on Stripe",
    primaryEnabled: input.canSign,
    primaryAction: "stripe",
    skip: true,
    blockedReason: input.canSign ? "" : ROYALTY_NOT_ALLOWED,
  };
}

export interface RoyaltySettingsView {
  visible: boolean;
  badge: { label: string; tone: "good" | "warn" | "neutral" };
  facts: string[];
  /** The button label, or null with nothing to press (or the person may not sign). */
  action: "Set up" | "Update bank account" | null;
  viewOnly: boolean;
  disclosure: string | null;
}

export function royaltySettingsView(input: {
  dto: RoyaltyAuthorizationDto | undefined;
  canSign: boolean;
}): RoyaltySettingsView {
  if (!royaltyOffered(input.dto)) {
    return {
      visible: false,
      badge: { label: "", tone: "neutral" },
      facts: [],
      action: null,
      viewOnly: false,
      disclosure: null,
    };
  }
  const phase = royaltyPhase(input.dto);
  const facts: string[] = [];
  const bank = bankLine(input.dto?.bankLast4);
  if (bank !== null) facts.push(bank);
  const signer = input.dto?.signerName ?? null;
  const signedOn = signedOnText(input.dto?.acceptedAt);
  if (signer !== null && signedOn !== null) facts.push(`Signed by ${signer}, ${signedOn}`);
  const label = phase === "on" ? "Update bank account" : "Set up";
  return {
    visible: true,
    badge:
      phase === "on"
        ? { label: "On", tone: "good" }
        : phase === "needs"
          ? { label: "Needs update", tone: "warn" }
          : { label: "To do", tone: "neutral" },
    facts,
    action: input.canSign ? (phase === "needs" ? "Update bank account" : label) : null,
    viewOnly: !input.canSign,
    disclosure: phase === "on" ? null : (input.dto?.mandate?.text ?? null),
  };
}

// ---------------------------------------------------------------------------------------------------
// Coming back from Stripe's hosted page
// ---------------------------------------------------------------------------------------------------

/** The marker Stripe's success and cancel addresses carry on the app root. */
export type RoyaltyArrival = "return" | "cancel";

export function readRoyaltyArrival(search: string): RoyaltyArrival | undefined {
  const value = new URLSearchParams(search).get("royalty_authorization");
  return value === "return" || value === "cancel" ? value : undefined;
}

/** The same address with the marker removed (everything else, including other keys and the hash, kept). */
export function withoutRoyaltyArrival(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("royalty_authorization");
  const rest = params.toString();
  return rest === "" ? "" : `?${rest}`;
}

/** Set (in sessionStorage, for this tab only) when Continue on Stripe is tapped from the wizard, so the return reopens
 * the wizard on the Royalty payments step instead of Settings. It carries nothing but the fact. */
export const ROYALTY_RETURN_MARKER_KEY = "bms-setup-wizard-royalty";

type MarkerStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function setRoyaltyReturnMarker(storage: MarkerStorage): void {
  try {
    storage.setItem(ROYALTY_RETURN_MARKER_KEY, "1");
  } catch {
    // Storage refused: the return falls back to Settings, which reads the same authorization.
  }
}

export function clearRoyaltyReturnMarker(storage: MarkerStorage): void {
  try {
    storage.removeItem(ROYALTY_RETURN_MARKER_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** True once for a marker that was set; the marker is gone after this call either way. */
export function takeRoyaltyReturnMarker(storage: MarkerStorage): boolean {
  try {
    const set = storage.getItem(ROYALTY_RETURN_MARKER_KEY) === "1";
    clearRoyaltyReturnMarker(storage);
    return set;
  } catch {
    return false;
  }
}
